<template>
  <div
    class="cart-summary-container"
    :class="{ 'signature-cart': type === 'signature' }"
  >
    <!-- Cart Summary Header -->
    <div class="cart-summary-header">
      <div
        class="d-flex ga-3"
        v-if="!cart.isEditing || (cart.isEditing && isMobile)"
      >
        <button
          type="button"
          aria-label="Edit cart name"
          @click="handleEditClick"
        >
          <img
            src="/images/icon/edit.svg"
            alt=""
            aria-hidden="true"
            class="edit-icon"
          />
        </button>
        <div class="cart-summary-title">
          <span class="cart-name" :title="cart.name">{{ cart.name }}</span>
          <span class="cart-activity-count"
            >• {{ cart.activityCount }}/{{
              cart.maxActivities
            }}
            Activities</span
          >
        </div>
      </div>

      <!-- Edit Mode - Desktop Only -->
      <div class="cart-name-edit-container" v-if="cart.isEditing && !isMobile">
        <div class="cart-name-input-wrapper">
          <input
            type="text"
            v-model="localName"
            class="cart-name-input"
            @keyup.enter="$emit('save-name', cart.id, localName)"
            @keyup.esc="$emit('cancel-edit-name', cart.id)"
            :ref="(el) => el && el.focus()"
          />
        </div>
        <div class="cart-name-edit-actions">
          <ActionButton
            variant="primary"
            size="large"
            :loading="isCartLoading"
            loadingText="Saving..."
            :disabled="isCartLoading"
            @click="$emit('save-name', cart.id, localName)"
          >
            <span>Save</span>
          </ActionButton>
          <ActionButton
            variant="text"
            size="large"
            :disabled="isCartLoading"
            @click="$emit('cancel-edit-name', cart.id)"
          >
            <span>Cancel</span>
          </ActionButton>
        </div>
      </div>

      <div class="cart-badge-container">
        <div class="cart-badge">
          <span v-if="cart.activityCount === 0">No Badge</span>
          <CustomTooltip
            placement="top"
            :text="badgeTooltipText"
            v-if="cart.activityCount > 0"
          >
            <template #trigger>
              <img :src="badgeImage" alt="Badge" class="cart-badge-img" />
            </template>
          </CustomTooltip>
        </div>
      </div>
    </div>

    <!-- Activity Progress Bars (Always visible) -->
    <div class="activity-progress-container">
      <div
        class="progress-bars-wrapper"
        :style="{
          'grid-template-columns': `repeat(${cart.maxActivities}, 1fr)`,
        }"
      >
        <div
          v-for="activityIndex in cart.maxActivities"
          :key="activityIndex"
          class="activity-progress-bar"
        >
          <div
            :class="[
              'progress-bar-fill',
              { filled: activityIndex <= cart.activityCount },
            ]"
          ></div>
          <div class="activity-label">Activity {{ activityIndex }}</div>
        </div>
      </div>
    </div>

    <!-- Divider Line -->
    <div class="cart-divider" :class="{ collapsed: !cart.isExpanded }"></div>

    <div
      class="d-flex"
      :class="{
        'justify-between': locale === 'US',
        'justify-end': locale !== 'US',
      }"
    >
      <!-- Cart Summary Details -->
      <div class="cart-summary-details" v-if="locale === 'US'">
        <div class="subtotal-with-quantity">
          <div class="subtotal">
            <span class="subtotal-label">Subtotal: </span>
            <span class="subtotal-amount"
              >${{ formatPrice(cart.subtotal) }}</span
            >
            <span class="subtotal-original-label" v-if="cart.originalPrice > 0">
              (${{ formatPrice(cart.originalPrice) }})</span
            >
          </div>
          <div class="quantity-controls" v-if="!quoteGenerated">
            <button
              type="button"
              class="quantity-btn quantity-decrease"
              @click="$emit('decrease-quantity', cart.id, type)"
              :disabled="cart.quantity <= 1 || isUpdatingQuantity"
              aria-label="Decrease quantity"
            >
              <svg
                width="15"
                height="15"
                viewBox="0 0 15 15"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.5 7.5H12.5"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <input
              type="number"
              class="quantity-input"
              aria-label="Quantity"
              :value="cart.quantity || 1"
              @input="$emit('quantity-input', cart.id, type, $event)"
              @blur="$emit('quantity-blur', cart.id, type, $event)"
              :disabled="isUpdatingQuantity"
              min="1"
            />
            <button
              type="button"
              class="quantity-btn quantity-increase"
              @click="$emit('increase-quantity', cart.id, type)"
              :disabled="isUpdatingQuantity"
              aria-label="Increase quantity"
            >
              <PlusIcon />
            </button>
          </div>
        </div>
      </div>
      <!-- Action Buttons -->
      <div class="cart-action-buttons" v-if="!quoteGenerated">
        <ActionButton
          variant="ghost"
          @click="$emit('delete-cart', cart.id)"
          class="action-button-delete"
        >
          <template #icon-right
            ><img
              src="/images/delete.svg"
              alt="Delete"
              class="action-icon action-icon-delete"
          /></template>
          <span style="color: var(--neutral-900)" class="activity-action-text"
            >Delete</span
          >
        </ActionButton>
        <ActionButton
          variant="secondary"
          @click="$emit('duplicate-cart', cart.id, type)"
          class="action-button-duplicate"
        >
          <span class="activity-action-text">Duplicate</span>
          <template #icon-right
            ><img
              src="/images/icon/copy.svg"
              alt="Duplicate"
              class="action-icon"
          /></template>
        </ActionButton>
      </div>
    </div>

    <!-- Content Section -->
    <div
      class="content-section"
      :id="`cart-content-${type}-${cart.id}`"
      :class="{
        collapsed: !cart.isExpanded,
        'align-center':
          cart.maxActivities === 8 || cart.activities.length === 0,
      }"
    >
      <div class="content-image" :class="{ 'flex-column': locale === 'SG' }">
        <img :src="cartImage" alt="Cart 3D" class="cart-3d-image" />
        <img
          :src="cartImage"
          alt="Cart 3D"
          class="cart-3d-image"
          v-if="locale === 'SG'"
        />
      </div>

      <div
        class="content-main"
        :style="{
          margin: cart.activities.length === 0 ? 'auto 0' : '0',
          alignItems: cart.activities.length === 0 ? 'center' : 'flex-start',
        }"
      >
        <!-- Activities List -->
        <ActivityList
          :activities="cart.activities"
          :maxActivities="cart.maxActivities"
          :current-cart-id="cart.id"
          :available-carts="availableCarts"
          :cart-type="type"
          @edit-activity="(payload) => $emit('edit-activity', payload)"
          @remove-activity="(payload) => $emit('remove-activity', payload)"
          @activity-converted="
            (payload) => $emit('activity-converted', payload)
          "
          v-if="cart.activities && cart.activities.length > 0"
        />

        <div
          class="add-activity-container"
          v-if="cart.activities.length === 0"
          @click="$emit('add-activity')"
          @keydown.enter.prevent="$emit('add-activity')"
          @keydown.space.prevent="$emit('add-activity')"
          role="button"
          tabindex="0"
          aria-label="Add activity"
        >
          <div class="add-activity-btn" aria-hidden="true">
            <span class="add-activity-text">Add Activity</span>
            <PlusIcon class="add-activity-icon" />
          </div>
          <p class="add-activity-subtitle">Choose from catalog</p>
        </div>
      </div>
    </div>

    <!-- View Toggle Button (individual for each cart) -->
    <button
      type="button"
      class="view-toggle-btn"
      :aria-expanded="cart.isExpanded"
      :aria-controls="`cart-content-${type}-${cart.id}`"
      @click="$emit('toggle-view', cart.id, type)"
    >
      <span>{{ cart.isExpanded ? "View Less" : "View More" }}</span>
      <img
        src="/images/icon/double-chevron-up.svg"
        alt=""
        aria-hidden="true"
        class="toggle-icon"
        :class="{ rotated: !cart.isExpanded }"
      />
    </button>

    <!-- Mobile Bottom Sheet for Edit Name -->
    <v-bottom-sheet
      v-model="showBottomSheet"
      class="cart-name-bottom-sheet"
      v-if="isMobile"
    >
      <v-card class="cart-name-bottom-sheet-card">
        <div class="cart-name-bottom-sheet-content">
          <!-- Header -->
          <div class="cart-name-bottom-sheet-header">
            <h3 class="cart-name-bottom-sheet-title">Change name</h3>
            <button
              type="button"
              aria-label="Close change name panel"
              class="cart-name-bottom-sheet-close"
              @click="closeBottomSheet"
            >
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="#3B4648"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
          </div>

          <!-- Input Field -->
          <div class="cart-name-bottom-sheet-input-wrapper">
            <input
              type="text"
              v-model="localName"
              class="cart-name-bottom-sheet-input"
              :ref="(el) => (bottomSheetInputRef = el)"
            />
          </div>

          <!-- Divider -->
          <div class="cart-name-bottom-sheet-divider"></div>

          <!-- Action Buttons -->
          <div class="cart-name-bottom-sheet-actions">
            <ActionButton
              variant="secondary"
              size="medium"
              :disabled="isCartLoading"
              @click="handleCancel"
              class="cart-name-bottom-sheet-cancel-btn"
            >
              <span>Cancel</span>
            </ActionButton>
            <ActionButton
              variant="primary"
              size="medium"
              :loading="isCartLoading"
              loadingText="Saving..."
              :disabled="isCartLoading"
              @click="handleSave"
              class="cart-name-bottom-sheet-save-btn"
            >
              <span>Save</span>
            </ActionButton>
          </div>
        </div>
      </v-card>
    </v-bottom-sheet>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";
import ActionButton from "@/components/common/ActionButton.vue";
import CustomTooltip from "@/components/common/CustomTooltip.vue";
import ActivityList from "@/components/maker-cart/ActivityList.vue";
import { PlusIcon } from "@/components/icons";
import { formatPrice } from "@/utils/index.js";
import { useLocaleStore } from "@/store/locale";

const props = defineProps({
  cart: {
    type: Object,
    required: true,
  },
  type: {
    type: String,
    default: "custom", // 'custom' or 'signature'
  },
  isCartLoading: {
    type: Boolean,
    default: false,
  },
  isUpdatingQuantity: {
    type: Boolean,
    default: false,
  },
  quoteGenerated: {
    type: Boolean,
    default: false,
  },
  availableCarts: {
    type: Array,
    default: () => [],
  },
});

const localeStore = useLocaleStore();
const locale = computed(() => localeStore.currentLocale);

const emit = defineEmits([
  "edit-name",
  "save-name",
  "cancel-edit-name",
  "decrease-quantity",
  "increase-quantity",
  "quantity-input",
  "quantity-blur",
  "delete-cart",
  "duplicate-cart",
  "toggle-view",
  "edit-activity",
  "remove-activity",
  "add-activity",
  "activity-converted",
]);

const localName = ref(props.cart.name);
const isMobile = ref(false);
const showBottomSheet = ref(false);
const bottomSheetInputRef = ref(null);

const checkMobile = () => {
  isMobile.value = window.innerWidth <= 768;
};

const handleEditClick = () => {
  if (isMobile.value) {
    showBottomSheet.value = true;
    nextTick(() => {
      if (bottomSheetInputRef.value) {
        bottomSheetInputRef.value.focus();
      }
    });
  } else {
    emit("edit-name", props.cart.id);
  }
};

const closeBottomSheet = () => {
  showBottomSheet.value = false;
  localName.value = props.cart.name;
};

const handleSave = () => {
  emit("save-name", props.cart.id, localName.value);
  closeBottomSheet();
};

const handleCancel = () => {
  closeBottomSheet();
  emit("cancel-edit-name", props.cart.id);
};

watch(
  () => props.cart.name,
  (newVal) => {
    localName.value = newVal;
  }
);

watch(showBottomSheet, (newVal) => {
  if (!newVal) {
    localName.value = props.cart.name;
  }
});

watch(
  () => props.cart.isEditing,
  (newVal) => {
    if (!newVal && isMobile.value) {
      showBottomSheet.value = false;
    }
  }
);

onMounted(() => {
  checkMobile();
  window.addEventListener("resize", checkMobile);
});

onUnmounted(() => {
  window.removeEventListener("resize", checkMobile);
});

const badgeTooltipText = computed(() => {
  if (props.type === "signature") {
    return "Savings more with signature Makercart!";
  }

  const activityCount = props.cart.activityCount;
  if (activityCount >= 4) {
    return "Best pricing unlocked!";
  } else if (activityCount >= 3) {
    return "You’re saving 7% — add 1 more for the best deal!";
  } else if (activityCount >= 2) {
    return "You’re saving 5% — add 2 more for the best deal!";
  } else {
    return "No savings yet — add more to save!";
  }
});

const badgeImage = computed(() => {
  if (props.type === "signature") {
    return "/images/maker-cart/signature-makercart-badge.png";
  }
  return `/images/maker-cart/makercart-badge-${props.cart.activityCount}.svg`;
});

const cartImage = computed(() => {
  if (props.type === "signature") {
    return "/images/maker-cart/cart-illustration-signature.svg";
  }

  if (props.cart.maxActivities === 8) {
    return "/images/maker-cart/cart-illustration-4.png";
  }

  return `/images/maker-cart/cart-illustration-${props.cart.activityCount}.png`;
});
</script>

<style scoped>
@import "@/assets/css/colors.css";

/* Cart Summary Container */
.cart-summary-container {
  width: 100%;
  background-color: var(--white);
  border: 1px solid var(--white-outline);
  border-radius: 16px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  transition: all 0.3s ease-in-out;
  opacity: 0;
  animation: fadeInSlideUp 0.5s ease-out forwards;
}

/* Signature Cart Styling */
.cart-summary-container.signature-cart {
  background-color: #e6f4ef !important;
}

@keyframes fadeInSlideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Cart Summary Header */
.cart-summary-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  gap: 12px;
}

.cart-summary-header > .d-flex {
  min-width: 0;
  flex: 1;
}

/* Cart Name Edit Container */
.cart-name-edit-container {
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
}

.cart-name-input-wrapper {
  flex-shrink: 0;
  width: 250px;
}

.cart-name-input {
  width: 100%;
  height: 56px;
  padding: 0 16px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background-color: var(--white);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 500;
  line-height: 24px;
  color: var(--black-body-text);
  outline: none;
  transition: border-color 0.2s ease;
}

.cart-name-input:focus {
  border-color: var(--primary-blue);
}

.cart-name-edit-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.cart-summary-title {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  flex: 1;
}

.cart-name {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 700;
  line-height: 28px;
  color: var(--black-body-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}

.cart-activity-count {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 400;
  line-height: 28px;
  color: var(--black-body-text);
  white-space: nowrap;
  flex-shrink: 0;
}

.cart-badge {
  text-align: right;
}

/* Cart Summary Details */
.cart-summary-details {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.subtotal-with-quantity {
  display: flex;
  gap: 12px;
  align-items: flex-end;
}

.subtotal {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-xl);
  line-height: 32px;
  color: var(--black-body-text);
  text-align: left;
}

.subtotal-label {
  font-weight: 400;
}

.subtotal-original-label {
  font-weight: 400;
  font-size: var(--fs-medium);
  line-height: 24px;
  color: var(--black-body-text);
  text-decoration: line-through;
}

.subtotal-amount {
  font-weight: 700;
}

.quantity-controls {
  display: flex;
  align-items: center;
  gap: 0;
}

.quantity-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border: none;
  background-color: transparent;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  color: var(--primary-blue);
  padding: 10px 16px;
}

.quantity-btn:hover:not(:disabled) {
  background-color: rgba(0, 61, 106, 0.05);
}

.quantity-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.quantity-btn svg {
  width: 14.518px;
  height: 14.518px;
}

.quantity-input {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border: 1px solid var(--primary-blue);
  border-radius: 12px;
  background-color: var(--white);
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  line-height: 24px;
  color: var(--primary-blue);
  padding: 10px 10px;
  text-align: center;
  outline: none;
  transition: all 0.2s ease;
}

.quantity-input:focus {
  border-color: var(--primary-blue);
  box-shadow: 0 0 0 2px rgba(0, 61, 106, 0.1);
}

.quantity-input::-webkit-inner-spin-button,
.quantity-input::-webkit-outer-spin-button {
  -webkit-appearance: none;
  appearance: none;
  margin: 0;
}

.quantity-input[type="number"] {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* Activity Progress Container */
.activity-progress-container {
  background-color: var(--neutral-100);
  border-radius: 8px;
  padding: 8px;
  overflow: hidden;
  transition: all 0.4s ease-in-out;
  max-height: 200px;
}

.activity-progress-container.collapsed {
  max-height: 0;
  padding: 0 8px;
  opacity: 0;
}

.progress-bars-wrapper {
  display: flex;
  gap: 8px;
  width: 100%;
  transition: opacity 0.3s ease-in-out;
}

.activity-progress-container.collapsed .progress-bars-wrapper {
  opacity: 0;
}

.activity-progress-bar {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.progress-bar-fill {
  width: 100%;
  height: 16px;
  background-color: var(--white-hover);
  border-radius: 8px;
  position: relative;
  overflow: hidden;
}

.progress-bar-fill.filled {
  background-color: var(--primary-green);
}

.activity-label {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-large);
  font-weight: 500;
  line-height: 32px;
  color: var(--black-body-text);
  text-align: center;
  white-space: nowrap;
}

/* Cart Divider */
.cart-divider {
  width: 100%;
  height: 1px;
  background-color: #e5e7eb;
  transition: all 0.4s ease-in-out;
  overflow: hidden;
  opacity: 1;
}

.cart-divider.collapsed {
  opacity: 0;
  height: 0;
}

/* Cart Action Buttons */
.cart-action-buttons {
  display: flex;
  gap: 8px;
  align-items: center;
}

.action-icon {
  width: 24px;
  height: 24px;
}

.action-icon-delete {
  filter: brightness(0);
  margin-bottom: 5px;
}

.action-icon img {
  margin-bottom: 0px !important;
}

/* Content Section */
.content-section {
  display: flex;
  gap: 16px;
  align-items: flex-start;
  width: 100%;
  transition: all 0.4s ease-in-out;
  opacity: 1;
  overflow: hidden;
}

.content-section.collapsed {
  opacity: 0;
  max-height: 0;
  padding: 0;
}

.content-section.align-center {
  align-items: center;
}

.content-image {
  width: 200px;
  height: auto;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
}

.cart-3d-image {
  width: 100%;
  height: auto;
  max-height: 100%;
  object-fit: contain;
}

/* Content Main */
.content-main {
  display: flex;
  width: 100%;
  min-height: 600px;
}

.add-activity-container {
  flex: 1;
  border: 2px dashed var(--border-dashed);
  border-radius: 16px;
  padding: 30px;
  height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  cursor: pointer;
}

.add-activity-btn {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 48px;
  height: 96px;
  border: none;
  background-color: transparent;
  border-radius: 16px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.add-activity-btn:hover {
  transform: translateY(-2px);
}

.add-activity-text {
  font-family: "Inter", sans-serif;
  font-size: var(--fs-xl);
  font-weight: 700;
  line-height: 32px;
  color: var(--primary-blue);
}

.add-activity-icon {
  width: 40px;
  height: 40px;
  color: var(--primary-blue);
}

.add-activity-subtitle {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 400;
  line-height: 24px;
  color: var(--neutral-700);
  text-align: center;
  margin: 0;
}

/* View Toggle Button */
.view-toggle-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 16px;
  height: 48px;
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 700;
  color: var(--primary-blue);
  transition: all 0.2s ease;
  border-radius: 12px;
  margin-top: auto;
}

.view-toggle-btn:hover {
  background-color: rgba(0, 61, 106, 0.05);
}

.toggle-icon {
  width: 16px;
  height: 16px;
  transition: transform 0.3s ease;
  filter: brightness(0) saturate(100%) invert(13%) sepia(74%) saturate(1718%)
    hue-rotate(200deg) brightness(96%) contrast(101%);
}

.toggle-icon.rotated {
  transform: rotate(180deg);
}

@media (max-width: 768px) {
  .cart-summary-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .subtotal-with-quantity .subtotal {
    display: none;
  }

  .subtotal {
    font-size: var(--fs-medium);
    font-style: normal;
    font-weight: 700;
    line-height: 24px;
    letter-spacing: 0.024px;
  }

  .quantity-input {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    font-size: 12px;
  }

  .quantity-btn {
    width: 32px;
    height: 32px;
    padding: 0;
  }

  .subtotal-amount {
    font-weight: 700;
  }

  .subtotal-original-label {
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: 0.048px;
  }

  .progress-bars-wrapper {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
  }

  .activity-progress-bar {
    flex-direction: row;
    align-items: center;
    gap: 12px;
  }

  .progress-bar-fill {
    flex: 1;
    height: 12px;
  }

  .activity-label {
    display: none;
    font-size: 18px;
    min-width: 80px;
    text-align: left;
  }

  .activity-progress-container {
    max-height: 400px;
  }

  .cart-action-buttons {
    width: 100%;
    justify-content: flex-end;
  }

  .content-section {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .content-main {
    width: 100%;
    min-height: auto;
    display: block;
  }

  .add-activity-container {
    height: 200px;
    padding: 20px;
  }

  .content-image {
    display: none;
  }

  .add-activity-btn {
    height: 80px;
    padding: 10px 24px;
  }

  .add-activity-text {
    font-size: var(--fs-big);
  }

  .add-activity-icon {
    width: 32px;
    height: 32px;
  }

  .cart-summary-container {
    padding: 16px;
    box-shadow:
      0px 1px 2px 0px rgba(0, 0, 0, 0.3),
      0px 2px 6px 2px rgba(0, 0, 0, 0.15);
  }

  .cart-name {
    font-size: var(--fs-medium);
    font-style: normal;
    font-weight: 700;
    line-height: 24px; /* 150% */
    letter-spacing: 0.024px;
  }

  .cart-activity-count {
    font-size: var(--fs-medium);
    font-style: normal;
    font-weight: 400;
    line-height: 24px;
    letter-spacing: 0.024px;
  }

  .cart-name-edit-container {
    flex-wrap: wrap;
    gap: 12px;
  }

  .cart-name-input-wrapper {
    width: 100%;
  }

  .cart-name-edit-actions {
    width: 100%;
    justify-content: space-between;
  }

  .cart-badge-container {
    width: 100%;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 10px;
  }

  .cart-badge img {
    height: 40px;
    object-fit: contain;
  }

  .activity-action-text {
    display: none;
  }

  .action-button-delete {
    border: 1px solid #40444b;
    padding: 8px 12px;
    height: 32px;
    min-width: 32px;
  }

  .action-icon-delete {
    margin-bottom: 0px;
  }

  .action-button-delete :deep(.action-button__content),
  .action-button-duplicate :deep(.action-button__content) {
    gap: 0px;
  }

  .action-button-duplicate {
    padding: 8px 12px;
    height: 32px;
    min-width: 32px;
  }

  .action-icon {
    width: 18px;
    height: 18px;
  }
}

/* Mobile Bottom Sheet Styles */
.cart-name-bottom-sheet {
  z-index: 10000001;
}

.cart-name-bottom-sheet-card {
  border-radius: 28px 28px 0 0 !important;
  overflow: hidden;
  background-color: #fff;
  box-shadow:
    0px 4px 8px 3px rgba(0, 0, 0, 0.15),
    0px 1px 3px 0px rgba(0, 0, 0, 0.1);
}

.cart-name-bottom-sheet-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 16px 24px;
}

.cart-name-bottom-sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 16px;
  border-bottom: 1px solid #d0d5dd;
  gap: 12px;
}

.cart-name-bottom-sheet-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-large);
  line-height: 28px;
  color: var(--primary-blue);
  margin: 0;
  flex: 1;
}

.cart-name-bottom-sheet-close {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
  transition: opacity 0.2s ease;
}

.cart-name-bottom-sheet-close:hover {
  opacity: 0.7;
}

.cart-name-bottom-sheet-close svg {
  width: 24px;
  height: 24px;
}

.cart-name-bottom-sheet-input-wrapper {
  width: 100%;
}

.cart-name-bottom-sheet-input {
  width: 100%;
  height: 56px;
  padding: 4px 16px;
  border: 1px solid #6c737f;
  border-radius: 8px;
  background-color: #fff;
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-medium);
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.024px;
  color: #3b4648;
  outline: none;
  transition: border-color 0.2s ease;
  box-sizing: border-box;
}

.cart-name-bottom-sheet-input:focus {
  border-color: var(--primary-blue);
}

.cart-name-bottom-sheet-divider {
  width: 100%;
  height: 1px;
  background-color: #d0d5dd;
}

.cart-name-bottom-sheet-actions {
  display: flex;
  gap: 8px;
  width: 100%;
}

.cart-name-bottom-sheet-cancel-btn,
.cart-name-bottom-sheet-save-btn {
  flex: 1;
  height: 40px;
  min-height: 40px;
  padding: 10px 16px;
  font-family: "League Spartan", sans-serif;
  font-weight: 700;
  font-size: var(--fs-small);
  line-height: 20px;
  letter-spacing: 0.014px;
}

.cart-name-bottom-sheet-cancel-btn {
  color: var(--primary-blue);
}

.cart-name-bottom-sheet-save-btn {
  color: #fff;
}

@media (max-width: 768px) {
  .cart-name-bottom-sheet-content {
    padding: 0 16px 24px;
  }
}
</style>
